import React, { useState, useEffect } from "react";
import { BaseDialog } from "../../shared/BaseDialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Button } from "@shared/ui/button";
import { FormService, FormField } from "@shared/services/FormServices";
import { Info } from "lucide-react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { toSnakeCase } from "@shared/utils/caseConverter";

interface LowercaseTransformationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetField: string;
  fileData: string[];
  onApplyTransformation: (config: any) => void;
}

const LowercaseTransformationDialog: React.FC<
  LowercaseTransformationDialogProps
> = ({ isOpen, onClose, targetField, fileData, onApplyTransformation }) => {
  const [formData, setFormData] = useState({
    applyTo: "entire",
    preserveSpacesPunctuation: true,
  });

  const [previewData, setPreviewData] = useState<{
    before: string[];
    after: string[];
  }>({
    before: [],
    after: [],
  });

  useEffect(() => {
    if (fileData) {
      const samples = fileData.filter((s) => s && s !== "...").slice(0, 2);
      setPreviewData({ before: samples, after: [] });
      updatePreview(
        { applyTo: "entire", preserveSpacesPunctuation: true },
        samples
      );
    }
  }, [fileData]);

  const fields: FormField[] = [
    {
      name: "applyTo",
      type: "radio",
      label: "Apply to",
      options: [
        { value: "entire", label: "Entire text" },
        { value: "keep_first_uppercase", label: "Keep first letter uppercase" }, // Changed from "keep-first"
      ],
      defaultValue: "entire",
      className: "col-span-1",
    },
    {
      name: "preserveSpacesPunctuation",
      type: "checkbox",
      label: "Preserve spaces/punctuation",
      defaultValue: true,
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields,
    mode: "onChange",
    defaultValues: formData,
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    const newFormData = { ...formData, [fieldName]: value };
    setFormData(newFormData);
    updatePreview(newFormData, previewData.before);
  };

  const updatePreview = (data: any, samples: string[]) => {
    const results = samples.map((item) => {
      switch (data.applyTo) {
        case "entire":
          return item.toLowerCase();
        case "keep_first_uppercase": // Changed from "keep_first"
          return item.length > 0
            ? item.charAt(0).toUpperCase() + item.slice(1).toLowerCase()
            : "";
        default:
          return item;
      }
    });
    setPreviewData({ before: samples, after: results });
  };

  const handleApply = () => {
    onApplyTransformation({
      targetField: toSnakeCase(targetField),
      transformation: {
        type: "lowercase",
        order: 1,
        config: { applyTo: formData.applyTo },
      },
    });
    onClose();
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Lowercase Configuration"
      className="max-w-4xl w-full max-h-[90vh] flex flex-col"
    >
      {/* Body */}
      <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
        {/* Info Banner */}
        <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 p-4 rounded-md">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Convert to Lowercase:</strong> Ideal for email addresses,
            usernames, or consistent lowercase formatting.
          </div>
        </div>

        <DynamicForm
          fields={fields}
          form={form}
          config={{
            fields,
            layout: "vertical",
            showSubmitButton: false,
            showResetButton: false,
            classes: FIELD_CLASSES,
          }}
          onFieldChange={handleFieldChange}
          gridColumns={1}
        />

        {/* Preview Section */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <h4 className="font-medium mb-3 text-gray-900">Preview</h4>
          <div className="grid grid-cols-2 gap-6">
            {/* Before */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Before</p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {previewData.before.length > 0 ? (
                  previewData.before.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-3 rounded-md border border-gray-200"
                    >
                      <span className="text-sm text-gray-800">{item}</span>
                    </div>
                  ))
                ) : (
                  <div className="bg-white p-3 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-400">
                      No data available
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* After */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">After</p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {previewData.after.length > 0 ? (
                  previewData.after.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-3 rounded-md border border-gray-200"
                    >
                      <span className="text-sm text-gray-800">{item}</span>
                    </div>
                  ))
                ) : (
                  <div className="bg-white p-3 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-400">
                      No preview available
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply}>Apply Transformation</Button>
        </div>
      </div>
    </BaseDialog>
  );
};

export default LowercaseTransformationDialog;
